/* eslint-disable lingui/no-unlocalized-strings -- nomes de dataset, chaves e caminhos do fio (U33); o texto de UI está todo em `msg` */
import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { formatCep } from '@/shared/format/cep';

import { legacyCents, legacyDate, legacyDateTime, legacyDocument, legacyPercentage, legacyReais } from './person.legacy-format';

// Catálogo de datasets de bureau por tipo de pessoa (P24, padrão `dataset-catalog`): o dono é `person`.
// Nasceu em `credit-note` (abas KYC e Crédito da operação) e foi trazido para cá sem mudar o conteúdo;
// `credit-note` passa a ler daqui pela porta pública (pendência da coordenação).
//
// Os datasets por pessoa das abas KYC e Crédito, como o legado lê e mostra (U02, U44). Cada entrada é
// um acordeão do legado, na mesma ordem e com o mesmo título, apontando para o mesmo `GET` de leitura
// (`useDatasetData` → `GET /{Pessoa}/{id}/Dataset/{nome}`, services/datasets/datasets.ts:15-26) e o
// mesmo `dataSet` que o componente procura na lista. A consulta nova (`POST`, paga) é o `request`, e só
// sai por ação humana com prévia (X01).
//
// Fontes: pages/NaturalPerson/NaturalPersonForm/Tabs/DatasetContainer/DatasetContainer.tsx (KYC PF),
// pages/LegalPerson/LegalPersonForm/Tabs/DatasetContainer/DatasetContainer.tsx (KYC PJ),
// components/Tabs/CreditTab/CreditTabContainer.tsx (Crédito PF e PJ) e components/Datasets/**.

export type PersonRecordType = 'NaturalPerson' | 'LegalPerson';
type Row = Readonly<Record<string, unknown>>;

/** Formatadores do legado com o idioma da tela e o "N/D". */
export interface DatasetFormat {
  readonly i18n: I18n;
  readonly nd: string;
  readonly text: (value: unknown) => string;
  readonly date: (value: unknown) => string;
  readonly dateTime: (value: unknown) => string;
  readonly yesNo: (value: unknown) => string;
  readonly cents: (value: unknown) => string;
  readonly reais: (value: unknown) => string;
  readonly doc: (value: unknown) => string;
}

export function datasetFormat(i18n: I18n): DatasetFormat {
  const nd = i18n._(msg`N/D`);
  const text = (value: unknown) => {
    if (value === null || value === undefined) return nd;
    const string = typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
    return string === '' ? nd : string;
  };
  return {
    i18n,
    nd,
    text,
    date: (value) => legacyDate(value, i18n.locale) ?? nd,
    dateTime: (value) => legacyDateTime(value, i18n.locale) ?? nd,
    yesNo: (value) => i18n._(value ? msg`Sim` : msg`Não`),
    cents: (value) => legacyCents(value, i18n.locale),
    reais: (value) => legacyReais(value, i18n.locale),
    doc: (value) => legacyDocument(value) ?? nd,
  };
}

export interface DatasetField {
  readonly label: MessageDescriptor;
  readonly value: (result: Row, f: DatasetFormat) => string;
}

export interface DatasetColumn {
  readonly id: string;
  readonly label: MessageDescriptor;
  readonly value: (row: Row, f: DatasetFormat) => string;
}

/** Uma parte do conteúdo: rótulo e valor (`HandleTitle`), tabela (`DataTable`) ou aviso (`Alert`). */
export type DatasetSection =
  | { readonly kind: 'fields'; readonly title?: MessageDescriptor; readonly fields: readonly DatasetField[] }
  | {
      readonly kind: 'table';
      readonly title?: MessageDescriptor;
      readonly rows: (result: Row) => readonly Row[];
      readonly columns: readonly DatasetColumn[];
      /** O "Detalhes" da linha (modal do legado), aberto na própria linha. */
      readonly details?: readonly DatasetField[];
    }
  | { readonly kind: 'alert'; readonly tone: 'info' | 'warn' | 'err'; readonly text: (result: Row, f: DatasetFormat) => string | null }
  | { readonly kind: 'link'; readonly label: MessageDescriptor; readonly href: string | null; readonly pending?: MessageDescriptor };

export interface PersonDatasetSpec {
  readonly id: string;
  readonly title: MessageDescriptor;
  /** Nome do dataset no `GET` e no `POST` (o legado usa o mesmo nos dois). */
  readonly dataset: string;
  /** Query do `GET`, quando o legado manda (`ClearSaleR6Fraud?getOnlyMostRecent=false`). */
  readonly params?: Readonly<Record<string, string | number | boolean>>;
  /** O `dataSet` que o componente procura; `null` = o primeiro item (ActivityIndicators). */
  readonly dataSet: string | null;
  /**
   * `wrapper`: `DatasetWrapper` (Gerado em + Atualizar; vazio = "Obter dados"). `summary`: o
   * `InfoMessage` + `DatasetSummaryPanel` dos indicadores de PJ ("Consultar novamente").
   */
  readonly frame: 'wrapper' | 'summary';
  /** Texto do vazio no quadro `summary` (nunca consultado / consultado sem dado). */
  readonly neverQueried?: MessageDescriptor;
  /** Conteúdo com dado (o que o legado desenha acima do wrapper). */
  readonly sections: (result: Row, item: Row, list: readonly Row[]) => readonly DatasetSection[];
  /** O legado lê `result` e, em alguns, `resultIntern`. */
  readonly hasData?: (item: Row) => boolean;
  /**
   * O "Atualizar" fica travado: no relatório de crédito o legado compara a data de `result.updatedAt`, que
   * não existe (o `result` é o link), e o botão nunca liga (PDFReport.tsx:31-34).
   */
  readonly refreshLocked?: boolean;
}

/** Texto de um valor do fio (texto, número ou booleano); objeto e vazio viram ''. */
const str = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '');
const asRow = (value: unknown): Row => (value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : {});
const asRows = (value: unknown): readonly Row[] => (Array.isArray(value) ? value.map(asRow) : []);
const at = (row: Row, path: string): unknown => path.split('.').reduce<unknown>((value, key) => asRow(value)[key], row);

const field = (label: MessageDescriptor, value: DatasetField['value']): DatasetField => ({ label, value });
const plain = (label: MessageDescriptor, path: string): DatasetField => field(label, (r, f) => f.text(at(r, path)));
const column = (id: string, label: MessageDescriptor, value?: DatasetColumn['value']): DatasetColumn => ({ id, label, value: value ?? ((row, f) => f.text(at(row, id))) });
const dateColumn = (id: string, label: MessageDescriptor): DatasetColumn => column(id, label, (row, f) => (at(row, id) ? f.date(at(row, id)) : ''));
const activeColumn = (id: string, label: MessageDescriptor): DatasetColumn => column(id, label, (row, f) => f.i18n._(at(row, id) === 'ACTIVE' ? msg`Ativo` : msg`Inativo`));

// ─── KYC · pessoa física e jurídica (componentes comuns) ────────────────────

/** `FraudRecords` (components/Datasets/FraudRecords/index.tsx): o histórico inteiro, uma linha por resultado. */
const fraud: PersonDatasetSpec = {
  id: 'fraud',
  title: msg`Consultar Fraude`,
  dataset: 'ClearSaleR6Fraud',
  params: { getOnlyMostRecent: false },
  dataSet: 'clearsale_r6_fraud',
  frame: 'wrapper',
  sections: (_result, _item, list) => [
    {
      kind: 'fields',
      fields: [
        field(msg`Registro de Consulta`, (r, f) => f.doc(r['registrationNumber'])),
        field(msg`Consultado em`, (r, f) => f.dateTime(at(r, 'responseAPI.createdAt'))),
      ],
    },
    {
      kind: 'table',
      // `tranformData` (FraudRecords/index.tsx:100-114): o histórico todo, uma linha por resultado de cada consulta.
      rows: () =>
        list.flatMap((entry) => {
          const result = asRow(entry['result']);
          return asRows(at(result, 'responseAPI.results')).map((row) => ({ ...row, requestType: result['requestType'] }));
        }),
      columns: [
        column('requestType', msg`Tipo de Registro`, (row, f) => f.i18n._(row['requestType'] === 'Register' ? msg`Registro` : msg`Consulta`)),
        column('evidenceTypeDisplay', msg`Evidência`),
        column('fraudModalityDisplay', msg`Modalidade de Fraude`),
        column('relatedActivityDisplay', msg`Atividade Relacionada`),
        column('eventDateTime', msg`Data do Evento`, (row, f) => f.date(row['eventDateTime'])),
        column('createdAt', msg`Data do Report`, (row, f) => f.date(row['createdAt'])),
        column('institution', msg`Entidade Relatora`, (row, f) => f.text(at(row, 'institution.companyName'))),
        column('cnpj', msg`CNPJ da Entidade Relatora`, (row, f) => f.doc(at(row, 'institution.cnpj'))),
      ],
    },
  ],
};

const addresses = (recordType: PersonRecordType): PersonDatasetSpec => ({
  id: 'addresses',
  title: msg`Endereços`,
  dataset: recordType === 'NaturalPerson' ? 'AddressesPersonal' : 'AddressesLegalPersonal',
  dataSet: recordType === 'NaturalPerson' ? 'addresses_personal' : 'addresses_legalpersonal',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['addresses']),
      columns: [
        column('type', msg`Tipo`, (row, f) => {
          const type = row['type'];
          if (type === 'WORK') return f.i18n._(msg`Trabalho`);
          if (type === 'HOME') return f.i18n._(msg`Casa`);
          if (type === 'OFFICIAL REGISTRATION') return f.i18n._(msg`Registro oficial`);
          return type ? '-' : '';
        }),
        column('typology', msg`Logradouro`, (row) => `${str(row['typology'])} ${str(row['addressMain'])}`.trim()),
        column('number', msg`Número`),
        column('neighborhood', msg`Bairro`),
        column('zipCode', msg`CEP`, (row, f) => (row['zipCode'] ? (formatCep(str(row['zipCode'])) ?? f.text(row['zipCode'])) : '')),
        column('city', msg`Cidade`),
        column('state', msg`Estado`),
      ],
    },
  ],
});

/** `PesquisaProtesto` (components/Datasets/PesquisaProtesto.tsx). */
const protests: PersonDatasetSpec = {
  id: 'protests',
  title: msg`Protestos`,
  dataset: 'Pesquisaprotesto',
  dataSet: 'pesquisaprotesto',
  frame: 'wrapper',
  sections: (result) => [
    { kind: 'fields', fields: [plain(msg`Origem`, 'origin'), plain(msg`Status`, 'baseStatus'), plain(msg`Total de protestos`, 'totalProtests'), plain(msg`Soma total de protestos`, 'totalProtestsValue')] },
    {
      kind: 'table',
      rows: () => asRows(result['protestItems']),
      columns: [column('state', msg`Estado`), column('totalProtests', msg`Total de Protestos`), dateColumn('firstProtestDate', msg`Data primeiro protesto`), dateColumn('lastProtestDate', msg`Data último protesto`)],
    },
  ],
};

/** `KycAndCompliance` → `KycPepHistoryList` + `KycSanctionsHistoryList`. */
const kycData: PersonDatasetSpec = {
  id: 'kycData',
  title: msg`KYC e Compliance`,
  dataset: 'KycData',
  dataSet: 'kyc_data',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'fields',
      title: msg`PEP`,
      fields: [
        field(msg`PEP`, (r, f) => f.yesNo(r['isCurrentlyPEP'])),
        field(msg`Primeiro registro`, (r, f) => f.date(r['firstPEPOccurenceDate'])),
        field(msg`Último registro`, (r, f) => f.date(r['lastPEPOccurenceDate'])),
      ],
    },
    {
      kind: 'table',
      rows: () => asRows(result['pepHistory']),
      columns: [
        column('details.originalName', msg`Nome original`),
        column('details.pepName', msg`Nome de sanção`),
        column('level', msg`Nível`),
        column('jobTitle', msg`Cargo`),
        column('department', msg`Departamento`),
        column('motive', msg`Motivo`),
        column('source', msg`Fonte`),
        dateColumn('startDate', msg`Início`),
        dateColumn('endDate', msg`Fim`),
      ],
    },
    {
      kind: 'fields',
      title: msg`Sanções`,
      fields: [
        field(msg`Está sobre sanção`, (r, f) => f.yesNo(r['isCurrentlySanctioned'])),
        field(msg`Primeiro registro`, (r, f) => f.date(r['firstSanctionDate'])),
        field(msg`Último registro`, (r, f) => f.date(r['lastSanctionDate'])),
      ],
    },
    {
      kind: 'table',
      rows: () => asRows(result['sanctionsHistory']),
      columns: [
        column('originalName', msg`Nome original`),
        column('sanctionName', msg`Nome de sanção`),
        column('source', msg`Fonte`),
        column('type', msg`Tipo`),
        column('matchRate', msg`Similaridade`),
        dateColumn('startDate', msg`Início`),
        dateColumn('endDate', msg`Fim`),
      ],
    },
  ],
};

/** `ProcessDetails` (components/Datasets/InformationDetails/index.tsx): o modal "Informações detalhada". */
const PROCESS_DETAILS: readonly DatasetField[] = [
  plain(msg`Número`, 'number'),
  plain(msg`Tipo`, 'type'),
  field(msg`Valor`, (r, f) => f.cents(Number(r['value']) * 100)),
  plain(msg`Assunto`, 'mainSubject'),
  plain(msg`Tribunal`, 'courtName'),
  plain(msg`Instância`, 'courtLevel'),
  plain(msg`Distrito do tribunal`, 'courtDistrict'),
  plain(msg`Vara do tribunal`, 'judgingBody'),
  plain(msg`Status`, 'status'),
  plain(msg`Estado`, 'state'),
  plain(msg`Volumes`, 'numberOfVolumes'),
  plain(msg`Páginas`, 'numberOfPages'),
  plain(msg`Partes`, 'numberOfParties'),
  plain(msg`Movimentações`, 'numberOfUpdates'),
  plain(msg`Idade do processo em dias`, 'lawSuitAge'),
  field(msg`Valor total da causa extraído do processo`, (r, f) => f.cents(Number(r['value']) * 100)),
  field(msg`Data de publicação`, (r, f) => f.date(r['publicationDate'])),
  field(msg`Data de notificação das partes do processo`, (r, f) => f.date(r['noticeDate'])),
  field(msg`Data da úlitma movimentação`, (r, f) => f.date(r['lastMovementDate'])),
  field(msg`Data da captura inicial do processo`, (r, f) => f.date(r['captureDate'])),
  field(msg`Data da última atualização dos dados`, (r, f) => f.date(r['lastUpdate'])),
];

/** `JudicialProcesses` → `JudicialProcessesList` (components/Datasets/JudicialProcesses). */
const judicial: PersonDatasetSpec = {
  id: 'judicial',
  title: msg`Processos Judiciais e Administrativos`,
  dataset: 'JudicialProcesses',
  dataSet: 'judicial_processes',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'fields',
      fields: [
        plain(msg`Total de processos`, 'totalLawsuits'),
        plain(msg`Total de processos como autor`, 'totalLawsuitsAsAuthor'),
        plain(msg`Total de processos como réu`, 'totalLawsuitsAsDefendant'),
        field(msg`Data da primeira ocorrência de processo em que a pessoa está envolvida`, (r, f) => f.date(r['firstLawsuitDate'])),
        field(msg`Data da última ocorrência de processo em que a pessoa está envolvida`, (r, f) => f.date(r['lastLawsuitDate'])),
        field(msg`Soma do valor total das causas`, (r, f) => f.cents(r['sumTotalProcessesValue'])),
      ],
    },
    {
      kind: 'table',
      rows: () => asRows(result['processes']),
      columns: [column('status', msg`Status`), column('mainSubject', msg`Assunto`), column('type', msg`Tipo`), column('number', msg`Número`), column('courtName', msg`Tribunal`)],
      details: PROCESS_DETAILS,
    },
  ],
};

/** `PGFNList` (components/Datasets/Pgfn.tsx): a tabela aparece mesmo vazia; "Detalhes" abre a certidão. */
const pgfn: PersonDatasetSpec = {
  id: 'pgfn',
  title: msg`Procuradoria Geral da Fazenda Nacional`,
  dataset: 'Pgfn',
  dataSet: 'pgfn',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['items']),
      columns: [column('origin', msg`Origem`), column('baseStatus', msg`Status`)],
      details: [
        field(msg`Há pendências com a Procuradoria-Geral da Fazenda Nacional(PGFN)`, (r, f) => f.i18n._(r['pgfnClearance'] ? msg`Não` : msg`Sim`)),
        plain(msg`Tipo de consulta`, 'queryType'),
        plain(msg`Identificação do pagador de impostos`, 'taxPayer'),
        field(msg`Data de emissão`, (r, f) => f.date(r['emissionDate'])),
        field(msg`Data de validade`, (r, f) => f.date(r['validityDate'])),
      ],
    },
  ],
};

const phoneColumns = (dateField: string): readonly DatasetColumn[] => [
  column('number', msg`Número`),
  column('areaCode', msg`Cód. Área`),
  column('countryCode', msg`Cód. País`),
  column('type', msg`Tipo`),
  column('isActive', msg`Ativo`, (row, f) => f.yesNo(row['isActive'])),
  column('currentCarrier', msg`Operadora`),
  dateColumn(dateField, msg`Última passagem`),
];

const phones: PersonDatasetSpec = {
  id: 'phones',
  title: msg`Telefones`,
  dataset: 'PhonesList',
  dataSet: 'phones_list',
  frame: 'wrapper',
  sections: (result) => [{ kind: 'table', rows: () => asRows(result['phones']), columns: phoneColumns('globalLastPassageDate') }],
};

const relatedPhones: PersonDatasetSpec = {
  id: 'relatedPhones',
  title: msg`Telefones de relacionados`,
  dataset: 'RelatedPeoplePhonesList',
  dataSet: 'related_people_phones_list',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['phones']),
      columns: [
        column('countryCode', msg`Cód. País`),
        column('areaCode', msg`Cód. Área`),
        column('number', msg`Número`),
        column('type', msg`Tipo`),
        column('isActive', msg`Ativo`, (row, f) => f.yesNo(row['isActive'])),
        column('currentCarrier', msg`Operadora`),
        dateColumn('lastPassageDate', msg`Última passagem`),
      ],
    },
  ],
};

// ─── KYC · pessoa física ────────────────────────────────────────────────────

const personalBasicData: PersonDatasetSpec = {
  id: 'personalBasicData',
  title: msg`Consulta de CPF`,
  dataset: 'PersonalBasicData',
  dataSet: 'personal_basic_data',
  frame: 'wrapper',
  sections: () => [
    {
      kind: 'fields',
      fields: [
        plain(msg`Nome`, 'name'),
        plain(msg`Idade`, 'age'),
        field(msg`Documento`, (r, f) => f.doc(r['taxIdNumber'])),
        // `removeTimeZone` (PersonalBasicData.tsx:14-23): corta o offset e formata a data escrita.
        field(msg`Data de Nascimento`, (r, f) => (typeof r['birthDate'] === 'string' ? f.date(r['birthDate'].split('+')[0]) : f.nd)),
        plain(msg`Nacionalidade`, 'nationality'),
        field(msg`Falecido`, (r, f) => f.yesNo(r['dead'])),
        field(msg`Gênero`, (r, f) => f.i18n._(r['gender'] === 'M' ? msg`Masculino` : msg`Feminino`)),
        field(msg`Score do nome`, (r, f) => (typeof r['nameUniquenessScore'] === 'number' && r['nameUniquenessScore'] !== 0 ? `${String(r['nameUniquenessScore'] * 100)}%` : f.nd)),
        plain(msg`Origem`, 'taxIdOrigin'),
        plain(msg`Status`, 'taxIdStatus'),
        plain(msg`Qtd. processos`, 'totalLawsuits'),
      ],
    },
  ],
};

const emailReputation: PersonDatasetSpec = {
  id: 'emailReputation',
  title: msg`Consulta de Email`,
  dataset: 'EmailReputation',
  dataSet: 'email_reputation',
  frame: 'wrapper',
  sections: () => [
    {
      kind: 'fields',
      fields: [
        plain(msg`Score`, 'eaScore'),
        plain(msg`Nível`, 'fraudRisk'),
        plain(msg`Descritivo`, 'eaAdvice'),
        plain(msg`Razão`, 'eaReason'),
        plain(msg`Faixa`, 'eaRiskBand'),
        plain(msg`Existe`, 'emailExists'),
        plain(msg`Indústria`, 'sourceIndustry'),
        plain(msg`Tipo de fraude`, 'fraudType'),
      ],
    },
  ],
};

/** `scoreText` (RiskScore/Speedometer.tsx:6-35): a faixa do score. */
export function riskScoreBand(score: unknown): MessageDescriptor | null {
  if (typeof score !== 'number' || !score) return null;
  if (score < 10) return msg`Muito baixo`;
  if (score < 46) return msg`Baixo`;
  if (score < 80) return msg`Médio`;
  if (score < 90) return msg`Alto`;
  return msg`Muito alto`;
}

/** Os lados do pentágono (ConsumerRiskScore.tsx:34-49): o peso 1 é vermelho, 2 laranja e 3 verde. */
export const RISK_LINKS = [
  { key: 'documentVsPhone', label: msg`CPF × Celular` },
  { key: 'documentVsDevice', label: msg`CPF × Device` },
  { key: 'phoneVsDevice', label: msg`Celular × Device` },
  { key: 'phoneVsZipCode', label: msg`Celular × CEP` },
  { key: 'zipCodeVsDevice', label: msg`CEP × Device` },
  { key: 'emailVsDevice', label: msg`Email × Device` },
  { key: 'emailVsZipCode', label: msg`Email × CEP` },
  { key: 'documentVsZipCode', label: msg`CPF × CEP` },
  { key: 'documentVsEmail', label: msg`CPF × Email` },
  { key: 'emailVsPhone', label: msg`Email × Celular` },
] as const;

const RISK_WEIGHT = { 1: msg`Vínculo fraco`, 2: msg`Vínculo médio`, 3: msg`Vínculo forte` } as const;

const riskScore: PersonDatasetSpec = {
  id: 'riskScore',
  title: msg`Score de Risco`,
  dataset: 'ConsumerRiskScore',
  dataSet: 'consumer_risk_score',
  frame: 'wrapper',
  sections: (result, item) => [
    { kind: 'fields', fields: [field(msg`Score`, (_r, f) => { const band = riskScoreBand(item['score']); return band ? f.i18n._(band) : f.nd; })] },
    {
      kind: 'fields',
      fields: RISK_LINKS.map((link) =>
        field(link.label, (_r, f) => {
          const weight = at(result, `scoresByAttributes.${link.key}`);
          return weight === 1 || weight === 2 || weight === 3 ? f.i18n._(RISK_WEIGHT[weight]) : f.nd;
        }),
      ),
    },
  ],
};

const professionalData: PersonDatasetSpec = {
  id: 'professionalData',
  title: msg`Atividade Empresarial e Profissional`,
  dataset: 'ProfessionalData',
  dataSet: 'professional_data',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['jobs']),
      columns: [
        column('companyIdNumber', msg`CNPJ`, (row, f) => (row['companyIdNumber'] ? f.doc(row['companyIdNumber']) : '')),
        column('companyName', msg`Empresa`),
        column('sector', msg`Setor`),
        column('level', msg`Cargo`),
        activeColumn('status', msg`Status`),
        column('income', msg`Renda estimada`, (row, f) => (row['income'] === 0 ? f.i18n._(msg`Nenhuma`) : f.cents(row['income']))),
        dateColumn('startDate', msg`Início`),
        dateColumn('endDate', msg`Fim`),
      ],
    },
  ],
};

const webMentions: PersonDatasetSpec = {
  id: 'webMentions',
  title: msg`Menções na web`,
  dataset: 'WebMentions',
  dataSet: 'web_mentions',
  frame: 'wrapper',
  sections: () => [{ kind: 'fields', fields: [plain(msg`Total`, 'totalPassages'), plain(msg`Ruins`, 'badPassages'), plain(msg`Boas`, 'goodPassages')] }],
};

const financeData: PersonDatasetSpec = {
  id: 'financeData',
  title: msg`Informações Financeiras`,
  dataset: 'FinanceData',
  dataSet: 'finance_data',
  frame: 'wrapper',
  sections: (result) => [
    { kind: 'fields', fields: [plain(msg`Patrimonio Total Estimado`, 'totalAssets')] },
    {
      kind: 'fields',
      title: msg`Renda estimada mensal`,
      fields: [plain(msg`Atividade Empresaria`, 'companyOwnership'), plain(msg`Ministério do Trabalho`, 'mte'), plain(msg`IBGE`, 'ibge'), plain(msg`Externo`, 'externalProvider')],
    },
    {
      kind: 'table',
      rows: () => asRows(result['taxes']),
      columns: [
        column('year', msg`Ano`),
        column('status', msg`Status`),
        column('bank', msg`Banco`),
        column('agency', msg`Agência`),
        column('batch', msg`Lote`),
        column('isVipBranch', msg`Premium`, (row, f) => f.yesNo(row['isVipBranch'])),
        dateColumn('captureDate', msg`Captura`),
      ],
    },
  ],
};

const onlineNegotiation: PersonDatasetSpec = {
  id: 'onlineNegotiation',
  title: msg`Presença online`,
  dataset: 'OnlineNegotiation',
  dataSet: 'online_negotiation',
  frame: 'wrapper',
  sections: () => [
    {
      kind: 'fields',
      fields: [
        plain(msg`Utilização`, 'internetUsageLevelV3'),
        plain(msg`Vendas`, 'esellerV3'),
        plain(msg`Compras`, 'eshopperV3'),
        field(msg`Última captura`, (r, f) => f.date(r['lastWebPassageDate'])),
        plain(msg`Total de passagens`, 'totalWebPassages'),
      ],
    },
  ],
};

const charging: PersonDatasetSpec = {
  id: 'charging',
  title: msg`Presença em cobrança`,
  dataset: 'Charging',
  dataSet: 'charging',
  frame: 'wrapper',
  sections: () => [
    {
      kind: 'fields',
      fields: [
        field(msg`Em cobrança`, (r, f) => f.yesNo(r['isCurrentlyOnCollection'])),
        plain(msg`Total`, 'collectionOccurrences'),
        plain(msg`Origens diferentes`, 'collectionOrigins'),
        field(msg`Primeira cobrança`, (r, f) => f.date(r['firstCollectionDate'])),
        field(msg`Última cobrança`, (r, f) => f.date(r['lastCollectionDate'])),
        plain(msg`Quantidade nos últimos 30 dias`, 'last30DaysCollectionOrigins'),
        plain(msg`Quantidade nos últimos 90 dias`, 'last90DaysCollectionOrigins'),
        plain(msg`Quantidade no último ano`, 'last365DaysCollectionOrigins'),
      ],
    },
  ],
};

const socialBenefit: PersonDatasetSpec = {
  id: 'socialBenefit',
  title: msg`Programas de benefícios e Assistência Social`,
  dataset: 'SocialBenefit',
  dataSet: 'social_benefit',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['socialPrograms']),
      columns: [
        column('programName', msg`Programa`),
        column('programCountry', msg`Local`, (row) => `${str(row['programCountry'])} - ${str(row['programState'])}`),
        dateColumn('assistanceStartDate', msg`Início`),
        dateColumn('assistanceEndDate', msg`Fim`),
        column('assistanceAmount', msg`Valor recebido`, (row, f) => (row['assistanceAmount'] ? f.cents(Number(row['assistanceAmount']) * 100) : '')),
        column('totalInstallments', msg`Parcelas`),
      ],
    },
  ],
};

const publicJobs: PersonDatasetSpec = {
  id: 'publicJobs',
  title: msg`Serviços públicos`,
  dataset: 'PublicJobs',
  dataSet: 'public_jobs',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['jobs']),
      columns: [
        column('sector', msg`Setor`),
        column('companyIdNumber', msg`CNPJ`, (row, f) => (row['companyIdNumber'] ? f.doc(row['companyIdNumber']) : '')),
        column('companyName', msg`Companhia`),
        column('area', msg`Área`),
        column('level', msg`Cargo`),
        activeColumn('status', msg`Status`),
        column('income', msg`Renda`, (row, f) => (row['income'] ? f.cents(row['income']) : '')),
        dateColumn('startDate', msg`Início`),
        dateColumn('endDate', msg`Fim`),
      ],
    },
  ],
};

const pfAntecedente: PersonDatasetSpec = {
  id: 'pfAntecedente',
  title: msg`Certidão Antecedentes Criminais - Polícia Federal`,
  dataset: 'PfAntecedente',
  dataSet: 'pf_antecedente',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['certificates']),
      columns: [
        column('protocolNumber', msg`Protocolo`),
        column('idNumber', msg`CPF`, (row, f) => (row['idNumber'] ? f.doc(row['idNumber']) : '')),
        column('status', msg`Status`),
        column('certificateNumber', msg`Número`),
        column('emissionDate', msg`Emissão`),
        column('validUntil', msg`Valido até`),
      ],
      details: [plain(msg`Cetificado`, 'certificateText')],
    },
  ],
};

// ─── KYC · pessoa jurídica ──────────────────────────────────────────────────

/** `BasicData` (DatasetLegalPerson/BasicDataLegalPerson.tsx). */
const legalBasicData: PersonDatasetSpec = {
  id: 'legalBasicData',
  title: msg`Consulta de CNPJ`,
  dataset: 'LegalBasicData',
  dataSet: 'legal_basic_data',
  frame: 'wrapper',
  sections: () => [
    {
      kind: 'fields',
      fields: [
        plain(msg`Idade`, 'age'),
        field(msg`Fundação`, (r, f) => f.date(r['foundedDate'])),
        plain(msg`Razão social`, 'officialName'),
        plain(msg`Natureza Jurídica`, 'legalNatureDescription'),
        plain(msg`Regime Fiscal`, 'taxRegime'),
        plain(msg`Match da razão social(%)`, 'officialNameInputNameMatchPercentage'),
        plain(msg`Score da razão social`, 'officialNameUniquenessScore'),
        plain(msg`Nome fantasia`, 'tradeName'),
        plain(msg`Match do nome fantasia(%)`, 'tradeNameInputNameMatchPercentage'),
        plain(msg`Score do nome fantasia`, 'tradeNameUniquenessScore'),
        plain(msg`Origem`, 'taxIdOrigin'),
        plain(msg`Status`, 'taxIdStatus'),
        field(msg`Data do status`, (r, f) => f.date(r['taxIdStatusDate'])),
        plain(msg`Qtd. processos`, 'totalLawsuits'),
        field(msg`Capital Social`, (r, f) => legacyCurrency(r['shareCapital'] ?? 0, f)),
      ],
    },
  ],
};

/**
 * `PartnersLawsuitsList` (PartnersLawsuits/PartnersLawsuitsList.tsx:148-167). O legado troca os valores
 * de "réu" e "outros" (`asOther` no rótulo de réu, `asDef` no de outros): aqui sai o mesmo que lá, para a
 * conferência lado a lado bater (registrado no E8B-DEPARA-RELATORIO).
 */
const partnersLawsuits: PersonDatasetSpec = {
  id: 'partnersLawsuits',
  title: msg`Ações Judiciais relacionadas aos sócios`,
  dataset: 'PartnersLawsuits',
  dataSet: 'partners_lawsuits',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'fields',
      fields: [plain(msg`Total de processos como autor`, 'totalLawsuitsAsAuthor'), plain(msg`Total de processos como réu`, 'totalLawsuitsAsOther'), plain(msg`Total de processos como outros`, 'totalLawsuitsAsDefendant')],
    },
    {
      kind: 'table',
      rows: () => asRows(result['lawsuits']),
      columns: [column('status', msg`Status`), column('mainSubject', msg`Assunto`), column('number', msg`Número`), column('courtName', msg`Tribunal`)],
      details: PROCESS_DETAILS,
    },
  ],
};

const sintegra: PersonDatasetSpec = {
  id: 'sintegra',
  title: msg`SINTEGRA - Sistema Integrado de Operações Interestaduais com Mercadorias e Serviços`,
  dataset: 'Sintegra',
  dataSet: 'sintegra',
  frame: 'wrapper',
  sections: () => [
    {
      kind: 'fields',
      fields: [
        plain(msg`CNPJ`, 'idNumber'),
        plain(msg`Estado`, 'status'),
        plain(msg`Razão social`, 'officialName'),
        plain(msg`Nome fantasia`, 'businessName'),
        plain(msg`Data de Fundação`, 'foundingDate'),
        plain(msg`Inscrição Estadual`, 'stateRegistration'),
        plain(msg`Núcleo do endereço`, 'addressCore'),
        plain(msg`Número de porta do endereço`, 'number'),
        plain(msg`Complemento do endereço`, 'complement'),
        plain(msg`Bairro do endereço`, 'neighborhood'),
        plain(msg`Cidade`, 'city'),
        plain(msg`Estado`, 'addressState'),
        plain(msg`CEP`, 'zipCode'),
        plain(msg`Natureza Legal`, 'legalnature'),
        plain(msg`Status`, 'status'),
        plain(msg`Situação fiscal`, 'fiscaloccurrence'),
        plain(msg`Posto de fiscalização`, 'fiscaloffice'),
        plain(msg`Regime de ICMS`, 'icmsregime'),
        plain(msg`Atividade principal`, 'maineconomicactivity'),
        plain(msg`Data de registro para emissão de NFe`, 'nferegistrationdt'),
        plain(msg`Casos de obrigatoriedade de NFe`, 'nfemandatoryindicator'),
        plain(msg`Início da obrigatoriedade de NFe`, 'nfemandatorystartdt'),
      ],
    },
  ],
};

const associatedPeople: PersonDatasetSpec = {
  id: 'associatedPeople',
  title: msg`Pessoas Relacionadas`,
  dataset: 'AssociatedPeople',
  dataSet: 'associated_people',
  frame: 'wrapper',
  sections: (result) => [
    {
      kind: 'table',
      rows: () => asRows(result['items']),
      columns: [
        column('relatedEntityTaxIdNumber', msg`Documento`),
        column('relatedEntityName', msg`Nome`),
        column('relationshipType', msg`Tipo`),
        column('relationshipName', msg`Relação`),
        column('relationshipLevel', msg`Nível`),
        dateColumn('relationshipStartDate', msg`Início`),
        dateColumn('relationshipEndDate', msg`Fim`),
      ],
    },
  ],
};

// ─── Crédito ────────────────────────────────────────────────────────────────

/** `Fgts` (DatasetNaturalPerson/FgtsList.tsx): erro da fonte vira aviso, e só sem erro sai o saldo. */
const fgts: PersonDatasetSpec = {
  id: 'fgts',
  title: msg`Saque aniversário`,
  dataset: 'Fgts',
  dataSet: 'fgts',
  frame: 'wrapper',
  sections: (result) => {
    const error = result['codigo'] ? `${str(result['codigo'])} - ${str(result['descricao'])}` : null;
    const error2 = result['erro'] ? `${str(result['erro'])} - ${str(result['mensagem'])}` : null;
    const sections: DatasetSection[] = [];
    if (result['existeSaldoDesconsiderado']) {
      sections.push({ kind: 'alert', tone: 'warn', text: (_r, f) => f.i18n._(msg`Existe saldo desconsiderado. Isso pode fazer com que o saldo disponível fique bloqueado para novos adiantamentos.`) });
    }
    if (error) sections.push({ kind: 'alert', tone: 'err', text: () => error });
    if (error2) {
      sections.push({
        kind: 'alert',
        tone: 'err',
        text: (_r, f) => (result['erro'] === '429' ? `429 - ${f.i18n._(msg`O serviço de consulta ao FGTS está indisponível no momento. Tente novamente em alguns segundos.`)}` : error2),
      });
    }
    if (error || error2) return sections;
    const periods = asRows(result['periodos']);
    return [
      ...sections,
      {
        kind: 'fields',
        fields: [
          plain(msg`Data do Saldo`, 'dataSaldo'),
          field(msg`Existe Saldo Desconsiderado`, (r, f) => f.yesNo(r['existeSaldoDesconsiderado'])),
          field(msg`Quantidade de Pedidos`, (r, f) => f.text(r['qtdPedidos'] ?? 0)),
          field(msg`Soma das Parcelas Disponíveis`, (r, f) =>
            Array.isArray(r['periodos']) ? f.reais(periods.slice(0, 10).reduce((sum, row) => sum + (typeof row['valor'] === 'number' ? row['valor'] : 0), 0)) : f.nd,
          ),
        ],
      },
      { kind: 'table', rows: () => periods, columns: [column('dataRepasse', msg`Data Repasse`), column('valor', msg`Valor`, (row, f) => (row['valor'] ? f.reais(row['valor']) : f.nd))] },
    ];
  },
};

/** `formatCurrency` (Currency.ts:48-61): o valor já em reais, só os dígitos. */
function legacyCurrency(value: unknown, f: DatasetFormat): string {
  const text = str(value);
  const digits = Number(text.replace(/\D/g, ''));
  return Number.isFinite(digits) ? f.reais(text.startsWith('-') ? -digits : digits) : f.reais(0);
}

/** `CnpjConsult` (DatasetLegalPerson/CnpjConsult/CnpjConsult.tsx): o mesmo `LegalBasicData` da KYC. */
const cnpjConsult: PersonDatasetSpec = {
  id: 'cnpjConsult',
  title: msg`Consulta de CNPJ`,
  dataset: 'LegalBasicData',
  dataSet: 'legal_basic_data',
  frame: 'wrapper',
  sections: () => [
    {
      kind: 'fields',
      fields: [
        plain(msg`Idade`, 'age'),
        field(msg`Fundação`, (r, f) => f.date(r['foundedDate'])),
        plain(msg`Razão Social`, 'officialName'),
        plain(msg`Natureza Jurídica`, 'legalNatureDescription'),
        plain(msg`Regime Fiscal`, 'taxRegime'),
        plain(msg`Match da razão social(%)`, 'officialNameInputNameMatchPercentage'),
        plain(msg`Score da razão social(%)`, 'officialNameUniquenessScore'),
        plain(msg`Nome Fantasia`, 'tradeName'),
        plain(msg`Match do nome fantasia(%)`, 'tradeNameInputNameMatchPercentage'),
        plain(msg`Score do nome fantasia`, 'tradeNameUniquenessScore'),
        plain(msg`Origem`, 'taxIdOrigin'),
        plain(msg`Status`, 'taxIdStatus'),
        field(msg`Data status`, (r, f) => f.date(r['taxIdStatusDate'])),
        plain(msg`Qtd. Processos`, 'totalLawsuits'),
        field(msg`Capital Social`, (r, f) => legacyCurrency(r['shareCapital'], f)),
      ],
    },
  ],
};

const summaryHasData = (item: Row) => !isEmptyValue(item['result']) || !isEmptyValue(item['resultIntern']);

/** `ActivityIndicators` (DatasetLegalPerson/ActivityIndicators.tsx): `Employees`, o primeiro item. */
const employees: PersonDatasetSpec = {
  id: 'employees',
  title: msg`Número de funcionários`,
  dataset: 'Employees',
  dataSet: null,
  frame: 'summary',
  neverQueried: msg`Os indicadores de atividade financeira ainda não foram consultados`,
  hasData: summaryHasData,
  sections: (result, item) => [
    { kind: 'fields', title: msg`Interno`, fields: [field(msg`Número de Funcionários`, (_r, f) => f.text(at(item, 'resultIntern.numberOfEmployees') || 0))] },
    { kind: 'fields', title: msg`Externo`, fields: [field(msg`Funcionários min.`, (_r, f) => f.text(result['minimumNumberOfEmployees'])), field(msg`Funcionários max.`, (_r, f) => f.text(result['maximumNumberOfEmployees']))] },
  ],
};

/** `FGTSRegularity` (DatasetLegalPerson/FGTSRegularity/FGTSRegularity.tsx). */
const fgtsRegularity: PersonDatasetSpec = {
  id: 'fgtsRegularity',
  title: msg`Regularidade de FGTS`,
  dataset: 'OndemandFgtsCompany',
  dataSet: 'ondemand_fgts_company',
  frame: 'summary',
  neverQueried: msg`Regularidade de FGTS ainda não foram consultada`,
  hasData: (item) => !isEmptyValue(item['result']),
  sections: () => [{ kind: 'fields', title: msg`Informações da regularidade`, fields: [plain(msg`Origem`, 'origin'), plain(msg`Status`, 'status')] }],
};

/** `PDFReport` (DatasetLegalPerson/PDFReport.tsx): o `result` é o link do relatório (ou "Em Execução"). */
const pdfReport: PersonDatasetSpec = {
  id: 'pdfReport',
  title: msg`Relatório de crédito`,
  dataset: 'pdfreport',
  dataSet: 'pdf_report',
  frame: 'wrapper',
  refreshLocked: true,
  hasData: (item) => typeof item['result'] === 'string' ? item['result'] !== '' : !isEmptyValue(item['result']),
  sections: (_result, item) => {
    const link = item['result'];
    return [{ kind: 'link', label: msg`Ver relatório`, href: typeof link === 'string' && link !== 'Em Execução' ? link : null, ...(link === 'Em Execução' ? { pending: msg`Em Execução` } : {}) }];
  },
};

/** `IncomeRangeCompany` (DatasetLegalPerson/IncomeRangeCompany/IncomeRangeCompany.tsx). */
const incomeRange: PersonDatasetSpec = {
  id: 'incomeRange',
  title: msg`Faturamento bruto`,
  dataset: 'IncomeRange',
  dataSet: 'incomerange',
  frame: 'summary',
  neverQueried: msg`Os indicadores de atividade financeira ainda não foram consultados`,
  hasData: summaryHasData,
  sections: (result, item) => [
    {
      kind: 'fields',
      title: msg`Interno`,
      fields: [field(msg`Faturamento min.`, (_r, f) => f.cents(at(item, 'resultIntern.minAnnualRevenue') ?? 0)), field(msg`Faturamento max.`, (_r, f) => f.cents(at(item, 'resultIntern.maxAnnualRevenue') ?? 0))],
    },
    { kind: 'fields', title: msg`Externo`, fields: [field(msg`Faturamento min.`, (_r, f) => f.cents(result['min'] ?? 0)), field(msg`Faturamento max.`, (_r, f) => f.cents(result['max'] ?? 0))] },
  ],
};

/** As linhas do "Consolidado" do SCR (Scr/ScrList.tsx), com os valores que o legado lê (inclusive o Prejuízo). */
const SCR_ROWS: readonly (readonly [MessageDescriptor, string, boolean])[] = [
  [msg`Vencer - Total`, 'vencerTotal', true],
  [msg`Até 30 dias e Vencidos até 14 dias`, 'vencerAte30diasVencidoAte14dias', false],
  [msg`31 a 60 dias`, 'vencer31a60dias', false],
  [msg`61 a 90 dias`, 'vencer61a90dias', false],
  [msg`91 a 180 dias`, 'vencer91a180dias', false],
  [msg`181 a 360 dias`, 'vencer181a360dias', false],
  [msg`Acima 360 dias`, 'vencerAcima360dias', false],
  [msg`Indeterminado`, 'vencerPrazoIndeterminado', false],
  [msg`Vencido - Total`, 'vencidoTotal', true],
  [msg`15 a 30 dias`, 'vencido15a30dias', false],
  [msg`31 a 60 dias`, 'vencido31a60dias', false],
  [msg`61 a 90 dias`, 'vencido61a90dias', false],
  [msg`91 a 180 dias`, 'vencido91a180dias', false],
  [msg`181 a 360 dias`, 'vencido181a360dias', false],
  [msg`Acima 360 dias`, 'vencidoAcima360dias', false],
  [msg`Prejuizo - Total`, 'vencidoTotal', true],
  [msg`Até 12 meses`, 'vencido15a30dias', false],
  [msg`Acima de 12 meses`, 'vencido15a30dias', false],
  [msg`Coobrigação - Total`, 'coobrigacaoTotal', true],
  [msg`Coobrigação Assumida`, 'coobrigacaoAssumida', false],
  [msg`Coobrigação Prestadas`, 'coobrigacaoPrestadas', false],
  [msg`Créditos a Liberar - Total`, 'creditosALiberarTotal', true],
  [msg`Créditos a Liberar até 360 dias`, 'creditosALiberarAte360dias', false],
  [msg`Créditos a Liberar acima de 360 dias`, 'creditosALiberarAcima360dias', false],
  [msg`Limites de Crédito - Total`, 'limiteCreditoTotal', true],
  [msg`Limite de Crédito com vencimento até 360 dias`, 'limiteCreditoAte360dias', false],
  [msg`Limite de Crédito com vencimento acima 360 dias`, 'limiteCreditoAcima360dias', false],
];

/** `SCRData` (Scr/ScrData.tsx), a visão "Consolidado". O "Detalhado" ainda abre no legado. */
const scr: PersonDatasetSpec = {
  id: 'scr',
  title: msg`Banco central - SCR`,
  dataset: 'ScrBcb',
  dataSet: 'scr_bcb',
  frame: 'wrapper',
  sections: (result) => {
    const sections: DatasetSection[] = asRows(result['listaDeMensagensDeValidacao']).map((message) => ({
      kind: 'alert' as const,
      tone: 'info' as const,
      text: () => `${str(message['codigo'])} - ${str(message['mensagem'])}`,
    }));
    const status = asRows(at(result, 'Status.scr_bcb'))[0];
    if (status?.['Message']) sections.push({ kind: 'alert', tone: 'err', text: () => str(status['Message']) });
    const base = result['dataBaseConsultada'];
    if (base) sections.push({ kind: 'fields', fields: [field(msg`Data-Base`, (_r, f) => yearMonthToMonthYear(base) ?? f.text(base))] });
    const flow = asRow(result['fluxoConsolidado']);
    if (result['fluxoConsolidado']) {
      sections.push({ kind: 'fields', title: msg`Consolidado`, fields: SCR_ROWS.map(([label, key]) => field(label, (_r, f) => f.reais(flow[key]))) });
    }
    return sections;
  },
};

function yearMonthToMonthYear(value: unknown): string | null {
  const text = typeof value === 'string' ? value.replace(/\D/g, '') : '';
  return text.length === 6 ? `${text.slice(4, 6)}/${text.slice(0, 4)}` : null;
}

/** `BoaVistaScore` (BoaVista/BoaVistaScore.tsx): as sete abas do legado como seções. */
const boaVista: PersonDatasetSpec = {
  id: 'boaVista',
  title: msg`Boa Vista Score`,
  dataset: 'BoaVistaScore',
  dataSet: 'boavista_score',
  frame: 'wrapper',
  sections: (result) => {
    const activeDebts = (str(at(result, 'debtsOccurrencesSummary.totalDebtor')) || '0');
    const queries90 = String(Number.parseInt((str(at(result, 'previousQueries90DaysSummary.total')) || '0'), 10) || 0);
    const na = (value: unknown, f: DatasetFormat) => (f.text(value) === f.nd ? f.i18n._(msg`Não informado`) : f.text(value));
    const location = asRow(result['location']);
    return [
      {
        kind: 'table',
        title: msg`Resultado da análise`,
        rows: () => asRows(result['scores']),
        columns: [
          column('score', msg`Score`, (row) => (str(row['score']) || '0')),
          column('protestosAtivos', msg`Protestos ativos`, (_row, f) => (activeDebts === '0' ? f.i18n._(msg`Nenhum`) : activeDebts)),
          column('consultas', msg`Consultas`, (_row, f) => f.i18n._(msg`${queries90} nos últimos 90 dias`)),
          column('text', msg`Renda Presumida`, (row, f) => (row['text'] ? str(row['text']) : f.i18n._(msg`Não informado`))),
          column('probability', msg`Probabilidade de pagamento`, (row, f) =>
            row['probability'] && row['probability'] !== '00000' ? legacyPercentage(Number.parseInt(str(row['probability']), 10) / 10000) : f.i18n._(msg`Não informado`),
          ),
        ],
      },
      {
        kind: 'fields',
        title: msg`Informações pessoais`,
        fields: [
          field(msg`Nome`, (r, f) => na(at(r, 'identification.name'), f)),
          field(msg`CPF`, (r, f) => (at(r, 'identification.document') ? f.doc(at(r, 'identification.document')) : f.i18n._(msg`Não informado`))),
          field(msg`Data de Nascimento`, (r, f) => (at(r, 'identification.birthDate') ? f.date(at(r, 'identification.birthDate')) : f.i18n._(msg`Não informado`))),
          field(msg`Nome da Mãe`, (r, f) => na(at(r, 'identification.motherName'), f)),
          field(msg`RG`, (r, f) => na(at(r, 'identification.rgNumber'), f)),
          field(msg`Órgão Emissor`, (r, f) => na(at(r, 'identification.issuingAgency'), f)),
          field(msg`Estado RG`, (r, f) => na(at(r, 'identification.rgState'), f)),
          field(msg`Nacionalidade`, (r, f) => na(at(r, 'identification.nationality'), f)),
          field(msg`Estado Civil`, (r, f) => na(at(r, 'identification.maritalStatus'), f)),
          field(msg`Dependentes`, (r, f) => na(at(r, 'identification.numberOfDependents'), f)),
          field(msg`Escolaridade`, (r, f) => na(at(r, 'identification.educationLevel'), f)),
          field(msg`Situação na Receita`, (r, f) => na(at(r, 'identification.federalRevenueStatus'), f)),
        ],
      },
      {
        kind: 'fields',
        title: msg`Localização`,
        fields: [
          field(msg`Endereço`, (_r, f) => {
            const parts = [location['streetType'], location['streetName'], location['streetNumber'], location['complement']].filter(Boolean).map(String);
            return parts.length > 0 ? parts.join(' ') : f.i18n._(msg`Não informado`);
          }),
          field(msg`Bairro`, (_r, f) => na(location['neighborhood'], f)),
          field(msg`Cidade/Estado`, (_r, f) => (location['city'] && location['state'] ? `${str(location['city'])} - ${str(location['state'])}` : f.i18n._(msg`Não informado`))),
          field(msg`CEP`, (_r, f) => na(location['zipCode'], f)),
          field(msg`Telefones`, (_r, f) => {
            const phones = [1, 2, 3]
              .filter((n) => location[`phone${String(n)}`] !== null && location[`phone${String(n)}`] !== undefined && location[`areaCode${String(n)}`] !== null && location[`areaCode${String(n)}`] !== undefined)
              .map((n) => `(${str(location[`areaCode${String(n)}`])}) ${str(location[`phone${String(n)}`])}`);
            return phones.length > 0 ? phones.join(', ') : f.i18n._(msg`Não informado`);
          }),
        ],
      },
      {
        kind: 'table',
        title: msg`Protestos`,
        rows: () => asRows(result['debts']),
        columns: [
          column('amount', msg`Valor`, (row, f) => f.cents(row['amount'])),
          column('situation', msg`Status`, (row, f) => f.i18n._(['C', 'A'].includes(str(row['situation'])) ? msg`Ativo` : msg`Inativo`)),
          column('occurrenceDate', msg`Data`, (row, f) => f.date(row['occurrenceDate'])),
          column('local', msg`Local`, (row) => `${str(row['city'])} - ${str(row['state'])}`),
          column('informant', msg`Informante`, (row, f) => na(row['informant'], f)),
          column('contract', msg`Contrato`, (row, f) => na(row['contract'], f)),
        ],
      },
      {
        kind: 'table',
        title: msg`Consultas anteriores`,
        rows: () => asRows(result['previousQueries']),
        columns: [
          column('date', msg`Data`, (row, f) => f.date(row['date'])),
          column('occurrenceType', msg`Tipo`, (row, f) => (row['occurrenceType'] ? str(row['occurrenceType']) : f.i18n._(msg`Não informado`))),
          column('informant', msg`Informante`, (row, f) => na(row['informant'], f)),
          column('product', msg`Produto`, (row, f) => na(row['product'], f)),
          column('amount', msg`Valor`, (row, f) => (typeof row['amount'] === 'number' && row['amount'] > 0 ? legacyCurrency(row['amount'], f) : f.i18n._(msg`Sem Valor atribuído`))),
        ],
      },
      { kind: 'fields', title: msg`Participações`, fields: [field(msg`Status do Consumidor`, (r, f) => (at(r, 'consumerStatus.message') ? str(at(r, 'consumerStatus.message')) : f.i18n._(msg`Não informado`)))] },
      {
        kind: 'fields',
        title: msg`Painel de comportamento`,
        fields: [
          field(msg`Comportamento de Pagamento`, (r, f) => {
            const score = at(r, 'positiveControlPanel.positivePaymentBehaviorIndicators.invoiceBehaviorScoreLatePayment.score');
            return score ? f.i18n._(msg`Score: ${str(score)}`) : f.i18n._(msg`Sem dados disponíveis`);
          }),
          field(msg`Comportamento de Consumo`, (r, f) => {
            const score = at(r, 'positiveControlPanel.positiveConsumptionBehaviorIndicators.recentContractsBehaviorScore.score');
            return score ? f.i18n._(msg`Score: ${str(score)}`) : f.i18n._(msg`Sem dados disponíveis`);
          }),
          field(msg`Consultas nos últimos 90 dias`, () => queries90),
          field(msg`Ações Cíveis`, (r, f) => f.i18n._(at(r, 'civilActionsSummary.registry') === 'S' ? msg`Possui` : msg`Nenhuma`)),
          field(msg`Débitos Ativos`, (_r, f) => (activeDebts === '0' ? f.i18n._(msg`Nenhum`) : activeDebts)),
          field(msg`Status Cadastro Positivo`, (r, f) => (at(r, 'consumerStatus.message') ? str(at(r, 'consumerStatus.message')) : f.i18n._(msg`Não participante`))),
        ],
      },
    ];
  },
};

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return true;
}

/** `!isEmpty(dataSet?.result)` do `DatasetWrapper` (DatasetWrapper/index.tsx:83), o padrão de "tem dado". */
export function datasetHasData(spec: PersonDatasetSpec, item: Row | undefined): boolean {
  if (!item) return false;
  return spec.hasData ? spec.hasData(item) : !isEmptyValue(item['result']);
}

/** O item que o componente do legado lê: pelo `dataSet` ou o primeiro. */
export function datasetItemOf(spec: PersonDatasetSpec, list: readonly Row[] | undefined): Row | undefined {
  if (!list) return undefined;
  return spec.dataSet === null ? list[0] : list.find((item) => item['dataSet'] === spec.dataSet);
}

// ─── o que cada acordeão lista ──────────────────────────────────────────────

/** `DatasetNaturalPersonContainer`: 19 acordeões, nesta ordem. */
export const KYC_NATURAL_PERSON: readonly PersonDatasetSpec[] = [
  fraud,
  personalBasicData,
  emailReputation,
  riskScore,
  addresses('NaturalPerson'),
  professionalData,
  webMentions,
  financeData,
  onlineNegotiation,
  charging,
  socialBenefit,
  publicJobs,
  pfAntecedente,
  protests,
  kycData,
  judicial,
  pgfn,
  phones,
  relatedPhones,
];

/** `DatasetLegalPersonContainer`: 12 acordeões, nesta ordem. */
export const KYC_LEGAL_PERSON: readonly PersonDatasetSpec[] = [fraud, legalBasicData, addresses('LegalPerson'), protests, kycData, judicial, partnersLawsuits, pgfn, sintegra, associatedPeople, phones, relatedPhones];

/**
 * O que não é `Dataset` na aba Crédito (CreditTabContainer.tsx:72-228): tokens de averbação, as
 * consultas de margem e o limite de crédito, com leitura própria.
 */
export type CreditExtra = 'creditLimit' | 'tokens' | 'siape' | 'zetra' | 'dataprev';

export type CreditEntry = { readonly kind: 'dataset'; readonly spec: PersonDatasetSpec } | { readonly kind: 'extra'; readonly id: CreditExtra; readonly title: MessageDescriptor };

export interface CreditTabFlags {
  /** `REQUEST_CREDIT`. */
  readonly requestCredit: boolean;
  /** `USE_SCR_DATASET`. */
  readonly scr: boolean;
  /** `(PF && consignado público SIAPE && personRegistrationNumber) || (PF && registrationNumber)`. */
  readonly marginQuery: boolean;
}

/** Os acordeões de uma pessoa na aba Crédito, na ordem do legado. */
export function creditEntries(recordType: PersonRecordType, flags: CreditTabFlags): CreditEntry[] {
  const pf = recordType === 'NaturalPerson';
  const entries: (CreditEntry | false)[] = [
    flags.requestCredit && { kind: 'extra', id: 'creditLimit', title: msg`Limite de Crédito` },
    pf && { kind: 'dataset', spec: fgts },
    pf && { kind: 'extra', id: 'tokens', title: msg`Consignado - Tokens de Averbação` },
    pf && flags.marginQuery && { kind: 'extra', id: 'siape', title: msg`Consultar margem - SIAPE` },
    pf && flags.marginQuery && { kind: 'extra', id: 'zetra', title: msg`Consultar margem - ZETRA` },
    pf && { kind: 'extra', id: 'dataprev', title: msg`Consultar margem - Crédito do trabalhador` },
    !pf && { kind: 'dataset', spec: employees },
    !pf && { kind: 'dataset', spec: cnpjConsult },
    !pf && { kind: 'dataset', spec: fgtsRegularity },
    !pf && { kind: 'dataset', spec: pdfReport },
    flags.scr && { kind: 'dataset', spec: scr },
    pf && { kind: 'dataset', spec: boaVista },
    !pf && { kind: 'dataset', spec: incomeRange },
  ];
  return entries.filter((entry): entry is CreditEntry => entry !== false);
}

/** Todos os datasets de pessoa (para o bloco e o contrato). */
export const PERSON_DATASETS: readonly PersonDatasetSpec[] = [
  ...KYC_NATURAL_PERSON,
  ...KYC_LEGAL_PERSON.filter((spec) => !KYC_NATURAL_PERSON.includes(spec)),
  addresses('LegalPerson'),
  fgts,
  employees,
  cnpjConsult,
  fgtsRegularity,
  pdfReport,
  scr,
  boaVista,
  incomeRange,
];

/** A chave do dataset na lista acima (`recordType:id`), que vai nos params do bloco. */
export const personDatasetKey = (recordType: PersonRecordType, spec: PersonDatasetSpec) => `${recordType}:${spec.id}`;

export function findPersonDataset(recordType: PersonRecordType, id: string): PersonDatasetSpec | undefined {
  if (id === 'addresses') return addresses(recordType);
  return PERSON_DATASETS.find((spec) => spec.id === id);
}
